import React from "react";
import { Badge, Box, Flex, IconButton, SimpleGrid, Text, Tooltip, useToast } from "@chakra-ui/react";
import { Link } from "react-router-dom";

import Error from "./error";
import Breadcrumbs from "./breadcrumbs";
import LoadMoreButton from "./load-more-button";
import { useSpaceXPaginated } from "../utils/use-space-x";

import { Star } from "react-feather";
import Favorites from "./favorites";
import { ACTIONS, useFavorites, useFavoritesUpdate, FAVORITES_TARGET} from "../contexts/favorites-context";

import { toastToggleFavorite } from './toasts';

const PAGE_SIZE = 12;

export default function LaunchPads() {
  const { data, error, isValidating, size, setSize } = useSpaceXPaginated(
    "/launchpads",
    {
      limit: PAGE_SIZE,
    }
  );

  const favorites = useFavorites(); 
  const setFavorites = useFavoritesUpdate();

  return (
    <div>
      <Flex alignItems="center" justifyContent="space-between">
        <Breadcrumbs
          items={[{ label: "Home", to: "/" }, { label: "Launch Pads" }]}
        />
        <Favorites target={FAVORITES_TARGET.PADS} />
      </Flex>
      <SimpleGrid m={[2, null, 6]} minChildWidth="350px" spacing="4">
        {error && <Error />}
        {data &&
          data
            .flat()
            .map((launchPad) => (
              <LaunchPadItem 
                key={launchPad.site_id} 
                launchPad={launchPad} 
                isFavorite={launchPad.id in favorites[FAVORITES_TARGET.PADS]}
                setFavorites={setFavorites}
              />
            ))}
      </SimpleGrid>
      <LoadMoreButton
        loadMore={() => setSize(size + 1)}
        data={data}
        pageSize={PAGE_SIZE}
        isLoadingMore={isValidating}
      />
    </div>
  );
}

const isFavoriteEqual = (prevProps, nextProps) => {
  return prevProps.isFavorite === nextProps.isFavorite;
}

// Using memo for optimization, with new data comes new renders ensuring data integrity (shouldComponentUpdate)
export const LaunchPadItem = React.memo(function({ launchPad, isFavorite, setFavorites }) {
  const toast = useToast();
  return (
    <Box
      boxShadow="md"
      borderWidth="1px"
      rounded="lg"
      overflow="hidden"
      position="relative"
    >
      <Flex p="2" justifyContent="space-around"> 
        <Box w="75%" as={Link} to={`/launch-pads/${launchPad.site_id}`}>
          <Box d="flex" alignItems="baseline">
            {launchPad.status === "active" ? (
              <Badge px="2" variant="solid" colorScheme="green">
                Active
              </Badge>
            ) : (
              <Badge px="2" variant="solid" colorScheme="red">
                Retired
              </Badge>
            )}
            <Box
              color="gray.500"
              fontWeight="semibold"
              letterSpacing="wide"
              fontSize="xs"
              textTransform="uppercase"
              ml="2"
            >
              {launchPad.attempted_launches} attempted &bull;{" "}
              {launchPad.successful_launches} succeeded
            </Box>
          </Box>

          <Box
            mt="1"
            fontWeight="semibold"
            as="h4"
            lineHeight="tight"
            isTruncated
          >
            {launchPad.name}
          </Box>
          <Text color="gray.500" fontSize="sm">
            {launchPad.vehicles_launched.join(", ")}
          </Text>
        </Box>
        <Flex w="25%" position="relative">
          <Tooltip 
            placement="left"
            label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          > 
            <IconButton 
              icon={<Star/>}
              position="absolute"
              right={0}
              top={0}
              variant="solid"
              colorScheme={isFavorite ? "orange" : "gray"} onClick={(e) => {
                e.preventDefault();   
                setFavorites({
                  type: isFavorite ? ACTIONS.DELETE : ACTIONS.ADD,
                  payload: { id: launchPad.id, data:launchPad, target: FAVORITES_TARGET.PADS}
                }); 
                toastToggleFavorite(toast, launchPad, FAVORITES_TARGET.PADS, isFavorite);       
              }}
            />
          </Tooltip>
        </Flex>
        
        
      </Flex>

    </Box>
  );
}, isFavoriteEqual)
